import { Box, Container, Divider, Grid, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import AdminDetailInfo from '../../../../backoffice/library/ui/components/AdminDetailInfo'
import OffersModal from '../../../../backoffice/offers/ui/components/modal/OffersModal'
import DetailHeroPanel from '../../../../shared/components/DetailHeroPanel'
import DetailInfoPanel from '../../../../shared/components/DetailInfoPanel'
import ButtonBack from '../../../../shared/components/buttons/ButtonBack'
import SupportFile from '../../../../shared/components/frontoffice/SupportFile'
import ShareIcon from '../../../../shared/components/icons/ShareIcon'
import VerifyAccessRoute from '../../../../shared/components/routes/VerifyAccessRoute'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import OffersRecommend from '../components/OffersRecommend'
import { useOfferDetail } from '../hooks/useOfferDetail'

const OfferImage = styled('img')(() => ({
  borderRadius: 8,
  maxWidth: '100%'
}))

const MediaSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(6),
  display: 'grid',
  gridGap: '24px'
}))

function OfferDetail() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)

  const {
    offer_selected,
    showForm,
    isAuth,
    authUser,
    favoriteState,
    location,
    infoDetail,
    handleFavoriteClick,
    handleGoBack,
    handleDownloadMedia,
    handleToggleForm
  } = useOfferDetail()

  if (!offer_selected) {
    return null
  }

  return (
    <Container maxWidth="lg">
      {showForm && <OffersModal />}

      {isAuth ? (
        <ButtonBack />
      ) : (
        <ButtonBack customText={t('see-offers-from-other-brands')} customFunction={handleGoBack} goBack={false} async={false} />
      )}

      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <VerifyAccessRoute visibility={offer_selected.visibility} />

          {location.pathname.includes('admin') && <AdminDetailInfo resourceSelected={offer_selected} />}

          {offer_selected.images && offer_selected.images.length > 1 ? (
            <Carousel centerMode autoPlay showThumbs={false} interval={5000} infiniteLoop>
              {offer_selected.images.map((image: any) => (
                <OfferImage
                  key={image.file_name}
                  src={`${import.meta.env.VITE_AWS_URL}/offers/${image.file_name}`}
                  alt={offer_selected.title}
                />
              ))}
            </Carousel>
          ) : (
            <Box borderRadius={8}>
              <OfferImage src={`${import.meta.env.VITE_AWS_URL}/offers/${offer_selected.image}`} alt={offer_selected.title} />
            </Box>
          )}

          {/* Downloadable material section */}
          {offer_selected.support_files && offer_selected.support_files.length > 0 && (
            <MediaSection>
              <Divider light />
              <Typography variant="subtitle1" color="primary">
                {t('downloadable-material')}
              </Typography>
              <Grid container spacing={isMobile ? 2 : 3}>
                {offer_selected.support_files.map((multimedia: any) => (
                  <SupportFile
                    key={multimedia.id}
                    data={multimedia}
                    type="offers"
                    vipContent={offer_selected.visibility === 1}
                    hasaccess={
                      offer_selected.visibility === 1 &&
                      (authUser.vip_lines.includes(offer_selected.line_id) ||
                        (offer_selected.line_id === null && authUser.vip_brands.includes(offer_selected.brand_id)) ||
                        [1, 2, 3, 5].includes(authUser.role_id))
                    }
                    handleDownload={handleDownloadMedia}
                  />
                ))}
              </Grid>
              <Divider light />
            </MediaSection>
          )}

          <OffersRecommend id={offer_selected.id} />
        </Grid>

        <Grid item xs={12} md={4}>
          <DetailHeroPanel
            visibility={offer_selected.visibility}
            status={offer_selected.status}
            brand={offer_selected.brand_id}
            title={offer_selected.title}
            author={offer_selected.created_by}
            description={offer_selected.description}
            edit={handleToggleForm}
            download={() => {
              window.open(`${import.meta.env.VITE_API_URL}/offers/download/${offer_selected.image}`)
            }}
            onClickFavorite={handleFavoriteClick(offer_selected.id)}
            favoriteState={favoriteState}
            share={
              <ShareIcon
                visibility={offer_selected.visibility}
                title={offer_selected.title}
                description={offer_selected.description}
                link={`${window.location.href}`}
                type="offer"
                multimedia={`${import.meta.env.VITE_AWS_URL}/offers/${offer_selected.image}`}
                component="buttonTextGreen"
                buttonTextGreen={t('share')}
              />
            }
          />
          <DetailInfoPanel attributes={infoDetail} />
        </Grid>
      </Grid>
    </Container>
  )
}

export default OfferDetail
